/** Lunghezze massime allineate ai campi card (editor + API). */
export const FIELD_LIMITS = {
  first_name: 16,
  last_name: 16,
  job_title: 50,
  email: 100,
  phone: 20,
  company_name: 32,
  company_motto: 60,
  vat_number: 11,
  sdi_code: 7,
  website: 100,
  "address.street": 32,
  "address.street_number": 10,
  "address.city": 32,
  "address.state": 10,
  secondary_email_label: 20,
  secondary_email: 100,
  secondary_phone_label: 20,
  secondary_phone_number: 20,
  social_username: 200,
  // Registrazione account
  register_first_name: 16,
  register_last_name: 16,
  register_company_name: 32,
  register_email: 100,
  password: 128,
} as const;

export type FieldLimitKey = keyof typeof FIELD_LIMITS;

export function getFieldLimit(field: string): number | undefined {
  return FIELD_LIMITS[field as FieldLimitKey];
}

export function clampToLimit(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return value.slice(0, maxLength);
}

export function clampFieldValue(field: string, value: string): string {
  const limit = getFieldLimit(field);
  if (limit === undefined) return value;
  return clampToLimit(value, limit);
}

/** Applica i limiti ai dati card letti dal server (es. record legacy troppo lunghi). */
export function sanitizeCardFromApi(card: Record<string, unknown>): Record<string, unknown> {
  const out = { ...card };
  const scalarFields = [
    "first_name",
    "last_name",
    "job_title",
    "email",
    "phone",
    "company_name",
    "company_motto",
    "vat_number",
    "sdi_code",
    "website",
  ] as const;
  for (const key of scalarFields) {
    if (typeof out[key] === "string") {
      out[key] = clampFieldValue(key, out[key] as string);
    }
  }
  if (out.address && typeof out.address === "object") {
    const addr = { ...(out.address as Record<string, string>) };
    for (const key of ["street", "street_number", "city", "state"] as const) {
      if (typeof addr[key] === "string") {
        addr[key] = clampFieldValue(`address.${key}`, addr[key]);
      }
    }
    out.address = addr;
  }
  if (Array.isArray(out.secondary_emails)) {
    out.secondary_emails = (
      out.secondary_emails as { label?: string; email?: string }[]
    ).map((row) => ({
      ...row,
      label:
        typeof row.label === "string"
          ? clampToLimit(row.label, FIELD_LIMITS.secondary_email_label)
          : row.label,
      email:
        typeof row.email === "string"
          ? clampToLimit(row.email, FIELD_LIMITS.secondary_email)
          : row.email,
    }));
  }
  if (Array.isArray(out.secondary_phones)) {
    out.secondary_phones = (
      out.secondary_phones as { label?: string; number?: string }[]
    ).map((row) => ({
      ...row,
      label:
        typeof row.label === "string"
          ? clampToLimit(row.label, FIELD_LIMITS.secondary_phone_label)
          : row.label,
      number:
        typeof row.number === "string"
          ? clampToLimit(row.number, FIELD_LIMITS.secondary_phone_number)
          : row.number,
    }));
  }
  if (Array.isArray(out.socials)) {
    out.socials = (out.socials as { platform?: string; username?: string }[]).map(
      (row) => ({
        ...row,
        username:
          typeof row.username === "string"
            ? clampToLimit(row.username, FIELD_LIMITS.social_username)
            : row.username,
      }),
    );
  }
  return out;
}
